import type { OpenPortfolioHolding, OpenPortfolioSummary } from '../../api/openPortfolioApi';
import type { ResultPoint } from '../Home/landing/samplePortfolio';
import { GROUPS, holdingMeta, type Group, type GroupKey, type HoldingMeta } from './holdingGroups';

/** Starting amounts the reader can pick. */
export const AMOUNTS = [5000, 10000, 25000, 50000, 100000];
export const DEFAULT_AMOUNT = 10000;

/** Dates the AI re-picked the holdings, marked on the chart. */
export const REPICK_DATES = ['2025-10-01'];

export { formatModelPortfolioDollarAmount as money } from '../Home/landing/samplePortfolio';

export function compactMoney(value: number): string {
  if (value >= 1000) {
    const k = Math.round(value / 100) / 10;
    return `$${String(k).replace(/\.0$/, '')}k`;
  }
  return `$${Math.round(value)}`;
}

export type WorstDrop = {
  /** Fraction, e.g. -0.26. */
  fall: number;
  peakIndex: number;
  troughIndex: number;
  /** First point back at or above the old high, or null if not yet. */
  recoveryIndex: number | null;
  /** Weeks from the high until it was passed again. */
  weeksToRecover: number | null;
  peakValue: number;
  troughValue: number;
};

/** The deepest fall from a previous high, and how long it took to climb back. */
export function worstDrop(points: ResultPoint[]): WorstDrop | null {
  if (points.length < 2) return null;
  let peak = 0;
  let worst: { fall: number; peak: number; trough: number } | null = null;
  for (let i = 0; i < points.length; i++) {
    if (points[i].portfolio > points[peak].portfolio) peak = i;
    const fall = points[i].portfolio / points[peak].portfolio - 1;
    if (!worst || fall < worst.fall) worst = { fall, peak, trough: i };
  }
  if (!worst || worst.fall >= 0) return null;
  const peakValue = points[worst.peak].portfolio;
  let recoveryIndex: number | null = null;
  for (let j = worst.trough + 1; j < points.length; j++) {
    if (points[j].portfolio >= peakValue) {
      recoveryIndex = j;
      break;
    }
  }
  return {
    fall: worst.fall,
    peakIndex: worst.peak,
    troughIndex: worst.trough,
    recoveryIndex,
    weeksToRecover: recoveryIndex === null ? null : recoveryIndex - worst.peak,
    peakValue,
    troughValue: points[worst.trough].portfolio,
  };
}

/** One flag per week: did the portfolio beat the S&P 500 that week? */
export function weeksAhead(summary: OpenPortfolioSummary): boolean[] {
  return summary.weeks.map((week) => week.qiReturn > week.spReturn);
}

/** Index of the first point on or after a date, or null when the date is outside the record. */
export function pointIndexOnOrAfter(points: ResultPoint[], iso: string): number | null {
  if (points.length < 2) return null;
  if (points[0].weekEnding && iso < points[0].weekEnding) return null;
  const index = points.findIndex((p, i) => i > 0 && p.weekEnding >= iso);
  return index > 0 ? index : null;
}

export type GroupSummary = Group & { key: GroupKey; weight: number; tickers: string[] };

/** Holdings rolled up into groups, largest first. */
export function groupSummaries(holdings: OpenPortfolioHolding[]): GroupSummary[] {
  const byKey = new Map<GroupKey, GroupSummary>();
  for (const h of holdings) {
    const key = holdingMeta(h.ticker, h.companyName).group;
    const g = byKey.get(key) ?? { ...GROUPS[key], key, weight: 0, tickers: [] };
    g.weight += Number(h.weight);
    g.tickers.push(h.ticker);
    byKey.set(key, g);
  }
  return Array.from(byKey.values()).sort((a, b) => b.weight - a.weight);
}

export type Tile = {
  holding: OpenPortfolioHolding;
  meta: HoldingMeta;
  group: GroupSummary;
  /** Position and size in percent of the map. */
  left: number;
  top: number;
  width: number;
  height: number;
};

type Box = { x: number; y: number; w: number; h: number };
type Item<T> = { value: number; data: T };

/** Splits items into two halves of near-equal weight along the longer side, recursively. */
function split<T>(items: Item<T>[], box: Box, out: Array<{ data: T; box: Box }>): void {
  if (items.length === 0) return;
  if (items.length === 1) {
    out.push({ data: items[0].data, box });
    return;
  }
  const total = items.reduce((t, i) => t + i.value, 0);
  let acc = 0;
  let cut = 1;
  let best = Infinity;
  for (let i = 0; i < items.length - 1; i++) {
    acc += items[i].value;
    const diff = Math.abs(acc - total / 2);
    if (diff < best) {
      best = diff;
      cut = i + 1;
    }
  }
  const a = items.slice(0, cut);
  const b = items.slice(cut);
  const f = total > 0 ? a.reduce((t, i) => t + i.value, 0) / total : 0.5;
  if (box.w >= box.h) {
    split(a, { x: box.x, y: box.y, w: box.w * f, h: box.h }, out);
    split(b, { x: box.x + box.w * f, y: box.y, w: box.w * (1 - f), h: box.h }, out);
  } else {
    split(a, { x: box.x, y: box.y, w: box.w, h: box.h * f }, out);
    split(b, { x: box.x, y: box.y + box.h * f, w: box.w, h: box.h * (1 - f) }, out);
  }
}

/**
 * A two-level tile map: groups first, then holdings inside each group.
 * Tile area is proportional to weight. `ratio` is the map's width / height.
 */
export function treemap(holdings: OpenPortfolioHolding[], ratio = 16 / 10.5): Tile[] {
  const W = 100 * ratio;
  const H = 100;
  const groups = groupSummaries(holdings);
  const placed: Array<{ data: GroupSummary; box: Box }> = [];
  split(
    groups.map((g) => ({ value: g.weight, data: g })),
    { x: 0, y: 0, w: W, h: H },
    placed,
  );
  const tiles: Tile[] = [];
  for (const { data: group, box } of placed) {
    const members = holdings
      .filter((h) => group.tickers.includes(h.ticker))
      .sort((a, b) => Number(b.weight) - Number(a.weight));
    const inner: Array<{ data: OpenPortfolioHolding; box: Box }> = [];
    split(
      members.map((h) => ({ value: Number(h.weight), data: h })),
      box,
      inner,
    );
    for (const { data: holding, box: b } of inner) {
      tiles.push({
        holding,
        meta: holdingMeta(holding.ticker, holding.companyName),
        group,
        left: (b.x / W) * 100,
        top: (b.y / H) * 100,
        width: (b.w / W) * 100,
        height: (b.h / H) * 100,
      });
    }
  }
  return tiles;
}
